import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Flag as ReportIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../context/LanguageContext';
import { userBookingBlocksAPI } from '../services/api';

/** User booking details: booking body rows visible at once before inner scroll (header is extra). */
const BOOKING_DETAILS_VIEWPORT_ROWS = 7;
const BOOKING_DETAILS_HEADER_PX = 56;
const BOOKING_DETAILS_ROW_PX = 48;
const BOOKING_DETAILS_TABLE_MAX_HEIGHT_PX =
  BOOKING_DETAILS_HEADER_PX + BOOKING_DETAILS_VIEWPORT_ROWS * BOOKING_DETAILS_ROW_PX;
/** Page size for details API — load enough rows to scroll inside the 7-row viewport. */
const BOOKING_DETAILS_PAGE_SIZE = 20;

const unwrapBody = (response) => response?.data?.data ?? response?.data?.payload ?? response?.data ?? {};

const extractList = (response) => {
  const data = unwrapBody(response);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
};

const extractTotal = (response, fallback = 0) => {
  const body = response?.data;
  const data = body?.data ?? body;
  return (
    data?.pagination?.total ??
    data?.total ??
    body?.pagination?.total ??
    body?.total ??
    fallback
  );
};

/** Path param for block / unblock / details — Swagger: appUserId */
const getAppUserId = (user) =>
  user?.appUserId ?? user?.app_user_id ?? user?.id ?? user?.userId ?? user?.customerId ?? user?.customerUserId;

const getRowDisplayName = (user) => {
  if (user?.displayName != null && String(user.displayName).trim()) return String(user.displayName).trim();
  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';
  const joined = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (joined) return joined;
  if (user?.name != null && String(user.name).trim()) return String(user.name).trim();
  if (user?.fullName != null && String(user.fullName).trim()) return String(user.fullName).trim();
  return '—';
};

const getEmail = (user) => user?.email || user?.emailId || user?.userEmail || '—';

const getTotalBookings = (user) =>
  user?.totalBookings ??
  user?.totalBooking ??
  user?.bookingCount ??
  user?.bookingsCount ??
  (Array.isArray(user?.bookings) ? user.bookings.length : 0);

const isUserBlocked = (user) => Boolean(user?.isBlocked ?? user?.is_blocked ?? user?.blocked);
const resolveBlockReasonFromBlock = (block) => {
  if (!block || typeof block !== 'object') {
    return null;
  }
  return block.reason ?? block.blockReason;
};

const getBlockReason = (user) => {
  const blockReason = resolveBlockReasonFromBlock(user?.block);
  const reason =
    user?.blockReason ??
    user?.blockedReason ??
    user?.reason ??
    user?.blockNote ??
    blockReason;
  if (reason == null) return '—';
  const normalized = String(reason).trim();
  return normalized || '—';
};

const buildDetailsSummary = (root) => {
  if (!root || typeof root !== 'object') {
    return {};
  }
  const nestedUser = root.user && typeof root.user === 'object' ? root.user : null;
  if (nestedUser) {
    return { ...nestedUser, ...root };
  }
  return { ...root };
};

const parseDetailsPayload = (response) => {
  const root = unwrapBody(response);
  const summary = buildDetailsSummary(root);
  const bookingsNode = summary?.bookings && typeof summary.bookings === 'object' ? summary.bookings : null;
  const items = Array.isArray(bookingsNode?.items) ? bookingsNode.items : [];
  const bookingsMeta = {
    page: bookingsNode?.page,
    pageSize: bookingsNode?.pageSize,
    total: bookingsNode?.total ?? items.length,
  };
  return { summary, bookingItems: items, bookingsMeta };
};

const formatSlotDate = (value) => {
  if (value == null || value === '') return '—';
  const s = String(value);
  const ymd = s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString(undefined, { dateStyle: 'medium' });
  }
  return s;
};

const formatSlotTimeRange = (b) => {
  const start = b?.slotStartTime ?? '';
  const end = b?.slotEndTime ?? '';
  if (!start && !end) return '—';
  if (start && end) return `${String(start).slice(0, 5)} – ${String(end).slice(0, 5)}`;
  return String(start || end);
};

const statusChipColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'CONFIRMED' || s === 'COMPLETED') return 'success';
  if (s === 'CANCELLED' || s === 'CANCEL') return 'error';
  if (s === 'PENDING') return 'warning';
  return 'default';
};

const getRequestErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error?.message || fallback;

const getDetailDisplayName = (detailSummary) => {
  if (!detailSummary) return '—';
  if (detailSummary.displayName != null && String(detailSummary.displayName).trim()) {
    return String(detailSummary.displayName).trim();
  }
  return getRowDisplayName(detailSummary);
};

const getDetailsDialogPaperSx = (isMobile) => ({
  maxHeight: isMobile ? '100%' : '90vh',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: isMobile ? 0 : 3,
});

function DetailBookingsTableBody({ detailBookings, translate }) {
  if (detailBookings.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={8} align="center">
          {translate('pages.userBookingBlocks.noBookings')}
        </TableCell>
      </TableRow>
    );
  }

  return detailBookings.map((booking, index) => (
    <TableRow key={String(booking?.bookingId ?? `booking-${index}`)}>
      <TableCell>{booking?.contactTitle ?? '—'}</TableCell>
      <TableCell>{booking?.customerName ?? '—'}</TableCell>
      <TableCell>{booking?.customerEmail ?? '—'}</TableCell>
      <TableCell>{booking?.customerPhone ?? '—'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatSlotDate(booking?.slotDate)}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatSlotTimeRange(booking)}</TableCell>
      <TableCell sx={{ maxWidth: 180 }}>{booking?.serviceAddress ?? '—'}</TableCell>
      <TableCell>
        <Chip
          label={booking?.status ?? '—'}
          size="small"
          color={statusChipColor(booking?.status)}
          variant="outlined"
        />
      </TableCell>
    </TableRow>
  ));
}

function UserDetailsDialogBody({
  translate,
  detailsLoading,
  detailName,
  detailEmail,
  detailBookings,
  detailBookingsMeta,
}) {
  if (detailsLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 2, flexShrink: 0 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {translate('pages.userBookingBlocks.table.displayName')}
          </Typography>
          <Typography>{detailName}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {translate('pages.userBookingBlocks.table.email')}
          </Typography>
          <Typography>{detailEmail}</Typography>
        </Box>
      </Box>

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, flexShrink: 0 }}>
        {translate('pages.userBookingBlocks.bookingsListTitle')}
        {detailBookingsMeta.total > 0 ? (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({translate('pages.userBookingBlocks.details.bookingsCount', { total: detailBookingsMeta.total })})
          </Typography>
        ) : null}
      </Typography>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          alignSelf: 'stretch',
          maxHeight: `${BOOKING_DETAILS_TABLE_MAX_HEIGHT_PX}px`,
          overflow: 'auto',
          width: '100%',
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: 960 }}>
          <TableHead>
            <TableRow>
              <TableCell>{translate('pages.userBookingBlocks.details.contactTitle')}</TableCell>
              <TableCell>{translate('pages.userBookingBlocks.details.customerName')}</TableCell>
              <TableCell>{translate('pages.userBookingBlocks.details.customerEmail')}</TableCell>
              <TableCell>{translate('pages.userBookingBlocks.details.customerPhone')}</TableCell>
              <TableCell>{translate('pages.userBookingBlocks.details.bookingDate')}</TableCell>
              <TableCell>{translate('pages.userBookingBlocks.details.slotTime')}</TableCell>
              <TableCell>{translate('pages.userBookingBlocks.details.serviceAddress')}</TableCell>
              <TableCell>{translate('pages.userBookingBlocks.details.bookingStatus')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DetailBookingsTableBody detailBookings={detailBookings} translate={translate} />
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function UserTableBody({ loading, rows, translate, onAccessSwitch, onViewUser, onReportUser }) {
  if (loading) {
    return (
      <TableRow>
        <TableCell colSpan={6} align="center">
          <Box sx={{ py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {translate('pages.userBookingBlocks.loading')}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  if (rows.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
          <Typography color="text.secondary">{translate('pages.userBookingBlocks.empty')}</Typography>
        </TableCell>
      </TableRow>
    );
  }

  return rows.map((user, index) => {
    const blocked = isUserBlocked(user);
    const userKey = getAppUserId(user) ?? `user-${index}`;
    return (
      <TableRow key={String(userKey)} hover>
        <TableCell>{getRowDisplayName(user)}</TableCell>
        <TableCell>{getEmail(user)}</TableCell>
        <TableCell>{getTotalBookings(user)}</TableCell>
        <TableCell>
          <Switch
            size="small"
            checked={blocked}
            onChange={(_, checked) => onAccessSwitch(user, checked)}
            color="warning"
          />
        </TableCell>
        <TableCell
          sx={{
            maxWidth: 260,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {getBlockReason(user)}
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Tooltip title={translate('pages.userBookingBlocks.viewDetails')}>
              <IconButton
                size="small"
                onClick={() => onViewUser(user)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={translate('pages.userBookingBlocks.reportUser') || 'Report user'}>
              <IconButton
                size="small"
                color="error"
                onClick={() => onReportUser(user)}
              >
                <ReportIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    );
  });
}

function UserDetailsDialog({
  open,
  onClose,
  isMobile,
  translate,
  detailsLoading,
  detailName,
  detailEmail,
  detailBookings,
  detailBookingsMeta,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: getDetailsDialogPaperSx(isMobile),
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>{translate('pages.userBookingBlocks.detailsTitle')}</DialogTitle>
      <DialogContent
        dividers
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <UserDetailsDialogBody
          translate={translate}
          detailsLoading={detailsLoading}
          detailName={detailName}
          detailEmail={detailEmail}
          detailBookings={detailBookings}
          detailBookingsMeta={detailBookingsMeta}
        />
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0, justifyContent: isMobile ? 'center' : 'flex-end', px: 3, py: 2 }}>
        <Button variant="contained" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#FF8C42', '&:hover': { bgcolor: '#E06526' } }}>
          {translate('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BlockUserDialog({
  open,
  onClose,
  isMobile,
  translate,
  blockTargetUser,
  blockReason,
  onBlockReasonChange,
  blockSubmitting,
  onConfirm,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle>{translate('pages.userBookingBlocks.blockDialog.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('pages.userBookingBlocks.blockDialog.subtitle')}
        </Typography>
        {blockTargetUser ? (
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
            {getRowDisplayName(blockTargetUser)} · {getEmail(blockTargetUser)}
          </Typography>
        ) : null}
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label={translate('pages.userBookingBlocks.blockDialog.reason')}
          placeholder={translate('pages.userBookingBlocks.blockDialog.reasonPlaceholder')}
          value={blockReason}
          onChange={onBlockReasonChange}
          disabled={blockSubmitting}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: isMobile ? 'center' : 'flex-end', gap: 1.5 }}>
        <Button onClick={onClose} disabled={blockSubmitting} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
          {translate('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onConfirm}
          disabled={blockSubmitting || !blockReason.trim()}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {blockSubmitting ? <CircularProgress size={22} /> : translate('pages.userBookingBlocks.blockDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReportUserDialog({
  open,
  onClose,
  isMobile,
  translate,
  reportTargetUser,
  reportReason,
  onReportReasonChange,
  reportDescription,
  onReportDescriptionChange,
  reportSubmitting,
  onConfirm,
}) {
  const reportReasonOptions = [
    { value: 'no_show', label: translate('pages.userBookingBlocks.reportReason.noShow') || 'No Show / Did Not Arrive' },
    { value: 'abusive', label: translate('pages.userBookingBlocks.reportReason.abusive') || 'Abusive or Offensive Behavior' },
    { value: 'harassment', label: translate('pages.userBookingBlocks.reportReason.harassment') || 'Harassment or Misconduct' },
    { value: 'spam', label: translate('pages.userBookingBlocks.reportReason.spam') || 'Spam or Fake Booking' },
    { value: 'misuse', label: translate('pages.userBookingBlocks.reportReason.misuse') || 'Misuse of Service' },
    { value: 'fraud', label: translate('pages.userBookingBlocks.reportReason.fraud') || 'Fraudulent Activity' },
    { value: 'other', label: translate('pages.userBookingBlocks.reportReason.other') || 'Other Reason' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle>{translate('pages.userBookingBlocks.reportDialog.title') || 'Report User'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('pages.userBookingBlocks.reportDialog.subtitle') || 'Select a reason and write a detailed description to submit a report against this user.'}
        </Typography>
        {reportTargetUser ? (
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
            {getRowDisplayName(reportTargetUser)} · {getEmail(reportTargetUser)}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="report-reason-select-label">
              {translate('pages.userBookingBlocks.reportDialog.reasonLabel') || 'Reason for reporting'}
            </InputLabel>
            <Select
              labelId="report-reason-select-label"
              id="report-reason-select"
              value={reportReason}
              label={translate('pages.userBookingBlocks.reportDialog.reasonLabel') || 'Reason for reporting'}
              onChange={onReportReasonChange}
              disabled={reportSubmitting}
            >
              {reportReasonOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={translate('pages.userBookingBlocks.reportDialog.descriptionLabel') || 'Description'}
            placeholder={translate('pages.userBookingBlocks.reportDialog.descriptionPlaceholder') || 'Write detailed description for reporting...'}
            value={reportDescription}
            onChange={onReportDescriptionChange}
            disabled={reportSubmitting}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: isMobile ? 'center' : 'flex-end', gap: 1.5 }}>
        <Button onClick={onClose} disabled={reportSubmitting} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
          {translate('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={reportSubmitting || !reportReason}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {reportSubmitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : (translate('pages.userBookingBlocks.reportDialog.confirm') || 'Submit Report')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const UserBookingBlocks = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailSummary, setDetailSummary] = useState(null);
  const [detailBookings, setDetailBookings] = useState([]);
  const [detailBookingsMeta, setDetailBookingsMeta] = useState({ total: 0, page: 1, pageSize: 20 });

  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockTargetUser, setBlockTargetUser] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockSubmitting, setBlockSubmitting] = useState(false);

  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTargetUser, setReportTargetUser] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const apiPage = page + 1;
      const usersRes = await userBookingBlocksAPI.getUsers(apiPage, rowsPerPage);
      const fetchedUsers = extractList(usersRes);
      setUsers(fetchedUsers);
      setTotalCount(extractTotal(usersRes, fetchedUsers.length));
    } catch (error) {
      toast.error(getRequestErrorMessage(error, t('pages.userBookingBlocks.toast.loadError')));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openBlockDialog = (user) => {
    setBlockTargetUser(user);
    setBlockReason('');
    setBlockDialogOpen(true);
  };

  const openReportDialog = (user) => {
    setReportTargetUser(user);
    setReportReason('');
    setReportDescription('');
    setReportDialogOpen(true);
  };

  const closeReportDialog = () => {
    if (!reportSubmitting) setReportDialogOpen(false);
  };

  const handleReportConfirm = async () => {
    if (!reportTargetUser) return;
    const id = getAppUserId(reportTargetUser);
    if (id === undefined || id === null) return;
    if (!reportReason) {
      toast.error(t('pages.userBookingBlocks.reportDialog.reasonRequired') || 'Please select a reason for reporting');
      return;
    }
    setReportSubmitting(true);
    try {
      if (typeof userBookingBlocksAPI.reportUser === 'function') {
        await userBookingBlocksAPI.reportUser(id, reportReason, reportDescription);
      }
      toast.success(t('pages.userBookingBlocks.toast.reported') || 'User report submitted successfully');
      setReportDialogOpen(false);
      setReportTargetUser(null);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, t('pages.userBookingBlocks.toast.reportError') || 'Failed to submit user report'));
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleBlockConfirm = async () => {
    if (!blockTargetUser) return;
    const id = getAppUserId(blockTargetUser);
    if (id === undefined || id === null) return;
    const reason = blockReason.trim();
    if (!reason) {
      toast.error(t('pages.userBookingBlocks.blockDialog.reasonRequired'));
      return;
    }
    setBlockSubmitting(true);
    try {
      await userBookingBlocksAPI.blockUser(id, reason);
      toast.success(t('pages.userBookingBlocks.toast.blocked'));
      setBlockDialogOpen(false);
      setBlockTargetUser(null);
      setBlockReason('');
      await fetchUsers();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, t('pages.userBookingBlocks.toast.toggleError')));
    } finally {
      setBlockSubmitting(false);
    }
  };

  /** Switch checked = user is blocked (`isBlocked` true). */
  const handleAccessSwitch = async (user, checked) => {
    const id = getAppUserId(user);
    if (id === undefined || id === null) return;
    if (checked) {
      openBlockDialog(user);
      return;
    }
    try {
      await userBookingBlocksAPI.unblockUser(id);
      toast.success(t('pages.userBookingBlocks.toast.unblocked'));
      await fetchUsers();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, t('pages.userBookingBlocks.toast.toggleError')));
    }
  };

  const handleViewUser = async (user) => {
    const appUserId = getAppUserId(user);
    if (appUserId === undefined || appUserId === null) return;
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailSummary(null);
    setDetailBookings([]);
    setDetailBookingsMeta({ total: 0, page: 1, pageSize: BOOKING_DETAILS_PAGE_SIZE });
    try {
      const detailsRes = await userBookingBlocksAPI.getUserDetails(appUserId, 1, BOOKING_DETAILS_PAGE_SIZE);
      const { summary, bookingItems, bookingsMeta } = parseDetailsPayload(detailsRes);
      setDetailSummary(summary);
      setDetailBookings(bookingItems);
      setDetailBookingsMeta({
        total: bookingsMeta.total ?? bookingItems.length,
        page: bookingsMeta.page ?? 1,
        pageSize: bookingsMeta.pageSize ?? BOOKING_DETAILS_PAGE_SIZE,
      });
    } catch (error) {
      toast.error(getRequestErrorMessage(error, t('pages.userBookingBlocks.toast.detailsError')));
      setDetailsOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const detailName = getDetailDisplayName(detailSummary);
  const detailEmail = detailSummary ? getEmail(detailSummary) : '—';

  const closeBlockDialog = () => {
    if (!blockSubmitting) setBlockDialogOpen(false);
  };

  const rows = useMemo(() => users, [users]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, p: { xs: 1, sm: 2 }, pr: { xs: 1, sm: 2 } }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
          p: { xs: 2, sm: 4 },
          borderRadius: { xs: 2, sm: 4 },
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: '1px solid rgba(255, 140, 66, 0.2)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: '#262829' }}>
            {t('pages.userBookingBlocks.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
            {t('pages.userBookingBlocks.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          fullWidth={isMobile}
          startIcon={<RefreshIcon />}
          onClick={fetchUsers}
          disabled={loading}
          sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, height: '46px', px: 3 }}
        >
          {t('pages.userBookingBlocks.refresh')}
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('pages.userBookingBlocks.table.displayName')}</TableCell>
                <TableCell>{t('pages.userBookingBlocks.table.email')}</TableCell>
                <TableCell>{t('pages.userBookingBlocks.table.totalBookings')}</TableCell>
                <TableCell>{t('pages.userBookingBlocks.table.bookingStatus')}</TableCell>
                <TableCell>{t('pages.userBookingBlocks.table.reason')}</TableCell>
                <TableCell align="right">{t('pages.userBookingBlocks.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <UserTableBody
                loading={loading}
                rows={rows}
                translate={t}
                onAccessSwitch={handleAccessSwitch}
                onViewUser={handleViewUser}
                onReportUser={openReportDialog}
              />
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <UserDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        isMobile={isMobile}
        translate={t}
        detailsLoading={detailsLoading}
        detailName={detailName}
        detailEmail={detailEmail}
        detailBookings={detailBookings}
        detailBookingsMeta={detailBookingsMeta}
      />

      <BlockUserDialog
        open={blockDialogOpen}
        onClose={closeBlockDialog}
        isMobile={isMobile}
        translate={t}
        blockTargetUser={blockTargetUser}
        blockReason={blockReason}
        onBlockReasonChange={(e) => setBlockReason(e.target.value)}
        blockSubmitting={blockSubmitting}
        onConfirm={handleBlockConfirm}
      />

      <ReportUserDialog
        open={reportDialogOpen}
        onClose={closeReportDialog}
        isMobile={isMobile}
        translate={t}
        reportTargetUser={reportTargetUser}
        reportReason={reportReason}
        onReportReasonChange={(e) => setReportReason(e.target.value)}
        reportDescription={reportDescription}
        onReportDescriptionChange={(e) => setReportDescription(e.target.value)}
        reportSubmitting={reportSubmitting}
        onConfirm={handleReportConfirm}
      />
    </Box>
  );
};

export default UserBookingBlocks;
